import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Inbox, CheckCircle2, TrendingUp, X, Mail, Phone, MapPin, Calendar, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, API } from "../../lib/api";
import { STATUS_LABELS, EVENT_TYPES } from "../../data/content";
import { toast } from "sonner";

const STATUS_COLORS = {
  nouvelle: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  a_rappeler: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  en_cours: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  proposition_envoyee: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  confirmee: "bg-green-500/20 text-green-300 border-green-500/30",
  refusee: "bg-red-500/20 text-red-300 border-red-500/30",
  archivee: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};
const eventLabel = (id) => EVENT_TYPES.find((e) => e.id === id)?.label || id;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get(`/quotes${filter !== "all" ? `?status=${filter}` : ""}`).then((r) => setQuotes(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/quotes/${id}/status`, { status });
    toast.success("Statut mis à jour");
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const statCards = [
    { label: "Total demandes", value: stats?.total ?? "—", Icon: Inbox },
    { label: "Nouvelles", value: stats?.new ?? "—", Icon: TrendingUp },
    { label: "Confirmées", value: stats?.confirmed ?? "—", Icon: CheckCircle2 },
    { label: "7 derniers jours", value: stats?.last7days ?? "—", Icon: Calendar },
  ];

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0d0d0f]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-kmr.jpg" alt="KMR" className="h-10 rounded-md" />
            <div><p className="font-serif text-xl">Administration</p><p className="text-xs text-muted-foreground">{user?.email}</p></div>
          </div>
          <div className="flex gap-3">
            <a href={`${API}/quotes/export/csv`} className="inline-flex items-center gap-2 text-sm glass px-4 py-2 rounded-full hover:text-gold transition-colors" data-testid="export-csv-btn"><Download size={16} /> CSV</a>
            <button onClick={doLogout} className="inline-flex items-center gap-2 text-sm glass px-4 py-2 rounded-full hover:text-gold transition-colors" data-testid="logout-btn"><LogOut size={16} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {statCards.map(({ label, value, Icon }) => (
            <div key={label} className="glass rounded-2xl p-6" data-testid="stat-card">
              <div className="flex items-center justify-between"><span className="text-muted-foreground text-sm">{label}</span><Icon size={20} className="text-gold" /></div>
              <p className="font-serif text-4xl mt-2 gold-text">{value}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setFilter("all")} className={`px-4 py-2 rounded-full text-sm border ${filter === "all" ? "border-gold text-gold bg-gold/10" : "border-white/10 text-foreground/70"}`} data-testid="filter-all">Toutes</button>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm border ${filter === k ? "border-gold text-gold bg-gold/10" : "border-white/10 text-foreground/70"}`} data-testid={`filter-${k}`}>{v}</button>
          ))}
        </div>

        <div className="glass rounded-2xl overflow-hidden">
          {quotes.length === 0 ? (
            <p className="text-center text-muted-foreground py-16" data-testid="no-quotes">Aucune demande pour ce filtre.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quotes-table">
                <thead className="text-left text-muted-foreground border-b border-white/10">
                  <tr><th className="p-4">N°</th><th className="p-4">Client</th><th className="p-4 hidden md:table-cell">Événement</th><th className="p-4 hidden lg:table-cell">Date</th><th className="p-4">Statut</th></tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} onClick={() => setSelected(q)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors" data-testid={`quote-row-${q.request_number}`}>
                      <td className="p-4 font-mono text-gold text-xs">{q.request_number}</td>
                      <td className="p-4">{q.firstName} {q.lastName}</td>
                      <td className="p-4 hidden md:table-cell">{eventLabel(q.eventType)}</td>
                      <td className="p-4 hidden lg:table-cell text-muted-foreground">{q.date || "—"}</td>
                      <td className="p-4"><span className={`px-3 py-1 rounded-full text-xs border ${STATUS_COLORS[q.status]}`}>{STATUS_LABELS[q.status]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={() => setSelected(null)} data-testid="quote-detail-panel">
          <div className="w-full max-w-lg bg-[#0d0d0f] h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div><p className="font-mono text-gold text-sm">{selected.request_number}</p><h2 className="font-serif text-2xl mt-1">{selected.firstName} {selected.lastName}</h2></div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-gold" data-testid="close-panel"><X size={24} /></button>
            </div>
            <div className="mb-6">
              <label className="text-xs text-muted-foreground">Statut</label>
              <select value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)} data-testid="status-select"
                className="w-full mt-1 bg-input/60 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-gold/60">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#0d0d0f]">{v}</option>)}
              </select>
            </div>
            <div className="space-y-3 text-sm">
              {[
                [Mail, selected.email], [Phone, selected.phone], [MapPin, `${selected.city || "—"} ${selected.postalCode || ""}`],
                [Calendar, selected.date || "Date non précisée"], [Users, selected.guests ? `${selected.guests} invités` : "—"],
              ].map(([Icon, val], i) => <div key={i} className="flex items-center gap-3 text-foreground/85"><Icon size={16} className="text-gold shrink-0" /> {val}</div>)}
            </div>
            <div className="mt-6 space-y-4 text-sm">
              <Detail label="Type d'événement" value={eventLabel(selected.eventType)} />
              <Detail label="Prestations" value={(selected.services || []).join(", ") || "—"} />
              <Detail label="Budget" value={selected.budget || "—"} />
              <Detail label="Ambiance" value={selected.ambiance || "—"} />
              <Detail label="Préférences musicales" value={selected.musicPreferences || "—"} />
              <Detail label="Moments particuliers" value={selected.specialMoments || "—"} />
              <Detail label="Description" value={selected.projectDescription || "—"} />
              <Detail label="Contraintes d'accès" value={selected.accessConstraints || "—"} />
              <Detail label="Entreprise" value={selected.company || "—"} />
              <Detail label="Contact préféré" value={`${selected.preferredContact || "—"} ${selected.callWindow ? `(${selected.callWindow})` : ""}`} />
              <Detail label="Newsletter" value={selected.newsletter ? "Oui" : "Non"} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="text-foreground/90 mt-0.5 whitespace-pre-wrap">{value}</p></div>;
}
